import { useEffect, useMemo } from 'react';
import { PricingTaxi, PricingTaxiQueryFilterMGT } from '../../../shared/types';
import { chunk, isEmpty } from 'lodash';
import { usePageState } from '../../../Hooks/usePageState';
import { useOverviewLinkState } from '../linkstate/pricingTaxiOverview.linkstate';
import { useGetPricingsTaxiQuery } from '../../../GraphQL/hooks/useGetPricingsTaxiQuery';

const pageSize = 10;
const getPageCount = (pricingsTaxi: PricingTaxi[]) => Math.ceil(pricingsTaxi?.length / pageSize);
const getCurrentPage = (requestedPage: number, pricingsTaxi: PricingTaxi[]) => {
    const pageCount = getPageCount(pricingsTaxi);
    return requestedPage < 1 ? 1 : requestedPage > pageCount ? pageCount : requestedPage;
};

export const useSortedZoneTimePricingTaxiQuery = () => {
    const { setLoading } = usePageState();
    const [state, setState] = useOverviewLinkState();
    const { page, search, selectedCities } = state;
    const request = useMemo(
        () =>
            ({
                search: search ?? '',
                cities: isEmpty(selectedCities) ? undefined : selectedCities,
            }) as PricingTaxiQueryFilterMGT,
        [search, selectedCities]
    );
    const { data: unsortedPricingsTaxi, refetch, loading } = useGetPricingsTaxiQuery(request);
    useEffect(() => setLoading(loading), [loading]);

    const setPage = (newPage: number) => {
        const pageCount = getPageCount(unsortedPricingsTaxi);
        const page = newPage < 1 ? 1 : newPage > pageCount ? pageCount : newPage;
        setState({ ...state, page });
    };

    const { pageCount, totalCount, sortedPricingsTaxi } = useMemo(() => {
        const totalCount = unsortedPricingsTaxi?.length ?? 0;
        const pageCount = getPageCount(unsortedPricingsTaxi);
        const sortedPricingsTaxi = chunk(unsortedPricingsTaxi, pageSize);

        return { totalCount, pageCount, sortedPricingsTaxi };
    }, [unsortedPricingsTaxi]);

    const currentPage = getCurrentPage(page, unsortedPricingsTaxi);

    return {
        sortedPricingsTaxi: sortedPricingsTaxi?.length > 0 ? sortedPricingsTaxi[currentPage - 1] : [],
        refetch: () => refetch({ request }),
        page: page ?? 1,
        pageCount,
        totalCount,
        pageSize,
        setPage,
        loading,
    };
};
